import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ICON_NAMES, COLORS, getIcon } from "@/lib/iconMap";

const EMPTY = {
  name: "", url: "", icon: "Globe", color: "bg-blue-500",
  category: "General", favorite: false, open_mode: "embedded", description: "",
};

export const AppFormDialog = ({ open, onOpenChange, onSubmit, initial, categories }) => {
  const [form, setForm] = useState(EMPTY);
  const [newCategory, setNewCategory] = useState("");
  const isEdit = Boolean(initial);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
      setNewCategory("");
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    let url = form.url.trim();
    if (url && !/^https?:\/\//i.test(url)) url = `https://${url}`;
    const category = newCategory.trim() || form.category || "General";
    onSubmit({ ...form, url, category });
  };

  const valid = form.name.trim() && form.url.trim();
  const SelectedIcon = getIcon(form.icon);
  const catOptions = Array.from(new Set([...(categories || []), "General"]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="app-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold tracking-tight">
            {isEdit ? "Edit App" : "Add New App"}
          </DialogTitle>
          <DialogDescription>
            Set a name, URL, icon, color and category for your web app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center gap-4 rounded-xl border border-border bg-muted/40 p-4">
            <div className={`${form.color} flex h-14 w-14 items-center justify-center rounded-xl text-white shadow-md`}>
              <SelectedIcon className="h-7 w-7" />
            </div>
            <div className="min-w-0">
              <p className="truncate font-semibold">{form.name || "App Name"}</p>
              <p className="truncate text-xs text-muted-foreground">{form.url || "example.com"}</p>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="app-name">Name</Label>
            <Input id="app-name" data-testid="form-name" value={form.name}
              onChange={(e) => set("name", e.target.value)} placeholder="e.g. Gmail" />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="app-url">URL</Label>
            <Input id="app-url" data-testid="form-url" value={form.url}
              onChange={(e) => set("url", e.target.value)} placeholder="https://mail.google.com" />
          </div>

          <div className="grid gap-2">
            <Label>Icon</Label>
            <div className="grid max-h-40 grid-cols-8 gap-2 overflow-y-auto rounded-xl border border-border p-2">
              {ICON_NAMES.map((name) => {
                const Ic = getIcon(name);
                const active = form.icon === name;
                return (
                  <button key={name} type="button" onClick={() => set("icon", name)}
                    data-testid={`icon-${name}`}
                    className={`flex h-9 items-center justify-center rounded-lg transition-colors ${
                      active ? "bg-primary text-primary-foreground" : "hover:bg-accent text-muted-foreground"
                    }`}>
                    <Ic className="h-4 w-4" />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button key={c} type="button" onClick={() => set("color", c)}
                  data-testid={`color-${c}`}
                  className={`${c} h-7 w-7 rounded-full transition-transform hover:scale-110 ${
                    form.color === c ? "ring-2 ring-offset-2 ring-foreground ring-offset-background" : ""
                  }`} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => { set("category", v); setNewCategory(""); }}>
                <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="new-cat">Or new category</Label>
              <Input id="new-cat" data-testid="form-new-category" value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)} placeholder="Create..." />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Open mode</Label>
            <Select value={form.open_mode} onValueChange={(v) => set("open_mode", v)}>
              <SelectTrigger data-testid="form-open-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="embedded">Inside launcher (embedded)</SelectItem>
                <SelectItem value="browser">New browser tab</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="app-desc">Description (optional)</Label>
            <Textarea id="app-desc" data-testid="form-description" value={form.description || ""}
              onChange={(e) => set("description", e.target.value)} rows={2} />
          </div>

          <div className="flex items-center justify-between rounded-xl border border-border p-3">
            <div>
              <Label className="cursor-pointer">Mark as favorite</Label>
              <p className="text-xs text-muted-foreground">Pin to the favorites section</p>
            </div>
            <Switch checked={form.favorite} onCheckedChange={(v) => set("favorite", v)} data-testid="form-favorite" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel">Cancel</Button>
          <Button onClick={handleSubmit} disabled={!valid} data-testid="form-submit">
            {isEdit ? "Save Changes" : "Add App"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
